import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, Video, Users, TrendingUp, FileText } from "lucide-react";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { format, isToday, isTomorrow, isThisWeek, isPast } from "date-fns";

interface MeetingWithProfiles {
  id: string;
  title: string;
  description?: string;
  meeting_date: string;
  duration_minutes: number;
  meeting_link?: string;
  organizer_id: string;
  organizer?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  };
  team_meeting_attendees: Array<{
    user_id: string;
    status: string;
    profiles?: {
      first_name?: string;
      last_name?: string;
      email?: string;
    };
  }>;
}

export const EnhancedTeamMeetings = () => {
  const [activeTab, setActiveTab] = useState("upcoming");

  useRealtimeSubscription("team_meetings", "team-meetings");

  const { data: meetings, isLoading } = useQuery({
    queryKey: ["all-team-meetings"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("team_meetings")
        .select(`
          *,
          organizer:profiles!team_meetings_organizer_id_fkey(
            first_name,
            last_name,
            email
          ),
          team_meeting_attendees(
            user_id,
            status,
            profiles(
              first_name,
              last_name,
              email
            )
          )
        `)
        .order("meeting_date", { ascending: true });

      if (error) throw error;
      return data as unknown as MeetingWithProfiles[];
    },
  });

  const upcomingMeetings = meetings?.filter(m => !isPast(new Date(m.meeting_date)));
  const pastMeetings = meetings?.filter(m => isPast(new Date(m.meeting_date)));
  const todayMeetings = meetings?.filter(m => isToday(new Date(m.meeting_date)));
  const tomorrowMeetings = meetings?.filter(m => isTomorrow(new Date(m.meeting_date)));
  const thisWeekMeetings = meetings?.filter(m => isThisWeek(new Date(m.meeting_date)) && !isToday(new Date(m.meeting_date)) && !isTomorrow(new Date(m.meeting_date)));

  const totalMeetings = meetings?.length || 0;
  const totalUpcoming = upcomingMeetings?.length || 0;
  const totalDuration = meetings?.reduce((sum, m) => sum + m.duration_minutes, 0) || 0;
  const avgDuration = totalMeetings > 0 ? Math.round(totalDuration / totalMeetings) : 0;

  const getMeetingBadge = (meetingDate: string) => {
    const date = new Date(meetingDate);
    if (isToday(date)) return <Badge className="bg-green-500/10 text-green-500">Today</Badge>;
    if (isTomorrow(date)) return <Badge className="bg-blue-500/10 text-blue-500">Tomorrow</Badge>;
    if (isThisWeek(date)) return <Badge className="bg-purple-500/10 text-purple-500">This Week</Badge>;
    return null;
  };

  const renderMeeting = (meeting: MeetingWithProfiles) => (
    <Card key={meeting.id} className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg">{meeting.title}</h3>
              {getMeetingBadge(meeting.meeting_date)}
              {meeting.meeting_link && (
                <Badge variant="outline" className="gap-1">
                  <Video className="h-3 w-3" />
                  Virtual
                </Badge>
              )}
            </div>
            {meeting.description && (
              <p className="text-sm text-muted-foreground mb-3">{meeting.description}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(meeting.meeting_date), "PPP")}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{format(new Date(meeting.meeting_date), "p")} ({meeting.duration_minutes}m)</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              Organizer: {meeting.organizer?.first_name} {meeting.organizer?.last_name}
            </span>
          </div>
          <Badge variant="secondary">
            {meeting.team_meeting_attendees?.length || 0} attendees
          </Badge>
        </div>

        {meeting.meeting_link && (
          <div className="mt-4">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => window.open(meeting.meeting_link, "_blank")}
            >
              <Video className="h-4 w-4 mr-2" />
              Join Meeting
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-1/2 mb-2" />
                <div className="h-8 bg-muted rounded w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Meetings</p>
                <p className="text-2xl font-bold">{totalMeetings}</p>
              </div>
              <Calendar className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Upcoming</p>
                <p className="text-2xl font-bold">{totalUpcoming}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Hours</p>
                <p className="text-2xl font-bold">{Math.round(totalDuration / 60)}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Duration</p>
                <p className="text-2xl font-bold">{avgDuration}m</p>
              </div>
              <FileText className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Filters */}
      {(todayMeetings?.length > 0 || tomorrowMeetings?.length > 0 || thisWeekMeetings?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {todayMeetings?.length > 0 && (
            <Card className="border-green-200 bg-green-50/50 dark:bg-green-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Badge className="bg-green-500">Today</Badge>
                  <span>{todayMeetings.length} meetings</span>
                </CardTitle>
              </CardHeader>
            </Card>
          )}
          {tomorrowMeetings?.length > 0 && (
            <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Badge className="bg-blue-500">Tomorrow</Badge>
                  <span>{tomorrowMeetings.length} meetings</span>
                </CardTitle>
              </CardHeader>
            </Card>
          )}
          {thisWeekMeetings?.length > 0 && (
            <Card className="border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Badge className="bg-purple-500">This Week</Badge>
                  <span>{thisWeekMeetings.length} meetings</span>
                </CardTitle>
              </CardHeader>
            </Card>
          )}
        </div>
      )}

      {/* Meetings List */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upcoming">
            Upcoming ({totalUpcoming})
          </TabsTrigger>
          <TabsTrigger value="past">
            Past ({pastMeetings?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-4 mt-4">
          {upcomingMeetings && upcomingMeetings.length > 0 ? (
            upcomingMeetings.map(renderMeeting)
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No upcoming meetings scheduled</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="past" className="space-y-4 mt-4">
          {pastMeetings && pastMeetings.length > 0 ? (
            pastMeetings.map(renderMeeting)
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No past meetings</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
